import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Ship, FileText, Calendar, Check, X, Pencil, MapPin, Plus, Trash2, Lock, Loader2, MoreVertical, MessageSquare, AlertTriangle, Users, FileSearch } from "lucide-react";
import { DossierSignatureBadge } from "@/components/embarque/DossierSignatureBadge";
import { ClienteSelectField } from "@/components/ClienteSelectField";
import { ProveedorSelectField } from "@/components/ProveedorSelectField";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useEmbarqueDocuments, useEmbarques, type Embarque, MODOS_TRANSPORTE, type ModoTransporte } from "@/hooks/useEmbarques";
import { useLiquidationGuard } from "@/hooks/useLiquidationGuard";
import { CompliancePill } from "@/components/embarque/CompliancePill";
import { useInventory, type DocumentRecord } from "@/hooks/useDocuments";
import { DocumentsTable } from "@/components/DocumentsTable";
import { AuditPanel } from "@/components/AuditPanel";
import { InventoryTable } from "@/components/InventoryTable";
import { cn } from "@/lib/utils";
import { LiquidacionExcepcionDialog, type ExcepcionPayload } from "@/components/LiquidacionExcepcionDialog";
import { ItemTraceabilityModal } from "@/components/ItemTraceabilityModal";
import { TrackingTab } from "@/components/TrackingTab";
import { AduanasTab } from "@/components/embarque/AduanasTab";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useOrgQuota } from "@/hooks/useOrgQuota";
import { PlanGate } from "@/components/PlanGate";
import { useTRM } from "@/hooks/useTRM";
import { useShipmentLandedCost, type TRMMap, type ShipmentGasto } from "@/hooks/useShipmentLandedCost";
import { computeWeightReconciliation, WEIGHT_RECONCILIATION_TOLERANCE_PCT } from "@/lib/weightReconciliation";
import { classifyExpense } from "@/lib/expenseClassifier";
import { useComplianceLookup } from "@/hooks/useComplianceLookup";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useGastosEmbarque, useCreateGasto, useDeleteGasto, useUpdateGasto, useLiquidarEmbarque, useFreezeLiquidation, type BaseProrrateo, type GastoEmbarque } from "@/hooks/useGastosEmbarque";
import { EditGastoSheet } from "@/components/EditGastoSheet";
import { ExpenseConflictResolver } from "@/components/embarque/ExpenseConflictResolver";
import { useExpenseConflicts } from "@/hooks/useExpenseConflicts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Props {
  embarque?: Embarque | null;
  embarqueId?: string | null;
  open?: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  defaultShowComments?: boolean;
}

import { normaliseTipo } from "@/lib/documentTypes";
import { ProveedorSmartBadge } from "@/components/ProveedorSmartBadge";
import { CommentsSheet } from "@/components/CommentsSheet";
import { OcrBaseAlertIcon } from "@/components/OcrBaseAlertIcon";

const REQUIRED_DOC_TYPES = [
  { key: "factura internacional", label: "Factura" },
  { key: "packing list", label: "Packing List" },
  { key: "bl", label: "BL" },
  { key: "declaración de importación", label: "Declaración" },
  { key: "certificado de origen", label: "Cert. Origen" },
];

const copFormatter = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0, maximumFractionDigits: 0 });
const usdFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

// ── Inline Add Gasto Form ──
function AddGastoForm({ embarqueId, onDone }: { embarqueId: string; onDone: () => void }) {
  const createGasto = useCreateGasto();
  const [concepto, setConcepto] = useState("");
  const [monto, setMonto] = useState("");
  const [moneda, setMoneda] = useState("COP");
  // ── Barrera 2: estado nullable. Forzamos clasificación humana cuando no hay
  //     sugerencia clara (compliance DIAN — no más default silencioso "Valor").
  const [base, setBase] = useState<BaseProrrateo | null>(null);
  const [baseTouched, setBaseTouched] = useState(false);
  const [trmPago, setTrmPago] = useState("");
  const [aplicaCosteo, setAplicaCosteo] = useState(true);

  // Auto-clasificación inteligente: respeta interacción humana (baseTouched).
  const classification = useMemo(() => classifyExpense(concepto), [concepto]);
  useEffect(() => {
    if (!baseTouched && classification.suggested && classification.suggested !== base) {
      setBase(classification.suggested);
    }
  }, [classification.suggested, baseTouched, base]);

  const isSubmitDisabled = createGasto.isPending || !concepto.trim() || !monto || base === null;

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;
    await createGasto.mutateAsync({
      embarque_id: embarqueId,
      concepto: concepto.trim(),
      monto: parseFloat(monto) || 0,
      moneda,
      base_prorrateo: base!,
      trm_pago: trmPago ? parseFloat(trmPago) : null,
      aplica_costeo_inventario: aplicaCosteo,
    });
    setConcepto("");
    setMonto("");
    setTrmPago("");
    setAplicaCosteo(true);
    setBase(null);
    setBaseTouched(false);
    onDone();
  };

  return (
    <div className="flex flex-wrap items-end gap-2 p-3 bg-muted/50 rounded-lg border border-border">
      <div className="flex-1 min-w-[140px]">
        <label className="text-[10px] text-muted-foreground">Concepto</label>
        <Input value={concepto} onChange={e => setConcepto(e.target.value)} placeholder="Agenciamiento, Almacenaje..." className="h-8 text-sm" />
      </div>
      <div className="w-28">
        <label className="text-[10px] text-muted-foreground">Monto</label>
        <Input type="number" value={monto} onChange={e => setMonto(e.target.value)} placeholder="0" className="h-8 text-sm" />
      </div>
      <div className="w-24">
        <label className="text-[10px] text-muted-foreground">Moneda</label>
        <Select value={moneda} onValueChange={setMoneda}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="COP">COP</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="w-32">
        <label className="text-[10px] text-muted-foreground flex items-center gap-1">
          Prorrateo
          {!baseTouched && classification.suggested && (
            <Badge variant="outline" className="h-3.5 px-1 text-[8px] font-normal border-primary/40 text-primary">
              auto
            </Badge>
          )}
          {base === null && (
            <Badge variant="outline" className="h-3.5 px-1 text-[8px] font-normal border-amber-500/60 text-amber-700 dark:text-amber-400 gap-0.5">
              <AlertTriangle className="h-2 w-2" /> Sin clasif.
            </Badge>
          )}
        </label>
        <Select
          value={base ?? ""}
          onValueChange={v => { setBase(v as BaseProrrateo); setBaseTouched(true); }}
        >
          <SelectTrigger className={cn("h-8 text-sm", base === null && "border-amber-500/60")}>
            <SelectValue placeholder="⚠ Peso, Valor o CBM" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Peso">Peso</SelectItem>
            <SelectItem value="Valor">Valor</SelectItem>
            <SelectItem value="CBM">CBM (Volumen)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="w-24">
        <label className="text-[10px] text-muted-foreground">TRM Pago</label>
        <Input type="number" value={trmPago} onChange={e => setTrmPago(e.target.value)} placeholder="Auto" className="h-8 text-sm" />
      </div>
      <div className="flex items-center gap-1 pb-1">
        <input type="checkbox" checked={aplicaCosteo} onChange={e => setAplicaCosteo(e.target.checked)} className="h-3.5 w-3.5" />
        <label className="text-[10px] text-muted-foreground whitespace-nowrap">Costeo</label>
      </div>
      <Button
        size="sm"
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
        title={base === null ? "Clasifica la base de prorrateo (Peso, Valor o CBM) — requerido por compliance DIAN" : undefined}
        className="h-8"
      >
        {createGasto.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

export function EmbarqueDetailModal({ embarque: embarqueProp, embarqueId, open: openProp, onClose, onOpenChange, defaultShowComments }: Props) {
  const quota = useOrgQuota();
  const { orgId } = useAuth();
  const { data: allEmbarques = [] } = useEmbarques();
  // 🔥 Fuente de verdad: SIEMPRE leer de la caché de React Query.
  // Si pasamos embarqueProp directamente, las actualizaciones optimistas (setQueryData)
  // no se reflejarían porque la prop "congela" la referencia. Sólo usamos embarqueProp
  // como fallback (id) para localizar la entidad en la caché.
  const targetId = embarqueProp?.id ?? embarqueId ?? null;
  const embarque = targetId
    ? (allEmbarques.find(e => e.id === targetId) ?? embarqueProp ?? null)
    : null;
  const isOpen = openProp ?? !!embarque;
  const handleClose = () => { onClose?.(); onOpenChange?.(false); };
  const { data: documents = [], isLoading: docsLoading } = useEmbarqueDocuments(embarque?.id ?? null);
  const documentIds = useMemo(() => documents.map((d: any) => d.id), [documents]);
  const { data: inventory = [], isLoading: invLoading } = useInventory(documentIds.length > 0 ? documentIds : undefined);
  const { trmMap, trm } = useTRM();
  const queryClient = useQueryClient();
  const { results: complianceResults, lookup: complianceLookup } = useComplianceLookup();

  // Gastos embarque
  const { data: gastos = [] } = useGastosEmbarque(embarque?.id ?? null);
  const deleteGasto = useDeleteGasto();
  const updateGasto = useUpdateGasto();
  const liquidarMutation = useLiquidarEmbarque();
  const freezeMutation = useFreezeLiquidation();
  const { hasUnresolved: hasConflicts } = useExpenseConflicts(embarque?.id ?? null);
  const [showAddGasto, setShowAddGasto] = useState(false);
  // TSK-209: Granular gasto editor (side-sheet)
  const [editingGasto, setEditingGasto] = useState<GastoEmbarque | null>(null);
  // Barrera 1 — diálogo de firma de excepción cuando hay mismatch de pesos
  const [showExcepcionDialog, setShowExcepcionDialog] = useState(false);
  // Barrera 3 — modal de trazabilidad por ítem
  const [traceabilityItemId, setTraceabilityItemId] = useState<string | null>(null);
  // Sub-tab controlado (Documentos Soporte / Costeo) — habilita deep-link interno
  // desde el banner de discrepancia de peso → "Verificar Documentos".
  const [innerTab, setInnerTab] = useState<"docs-soporte" | "costeo">("docs-soporte");
  // IDs de documentos a resaltar (pulso ámbar 3.5s) tras navegación desde banner.
  const [highlightDocIds, setHighlightDocIds] = useState<string[]>([]);

  // Inline edit title
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(embarque?.numero_do ?? "");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showComments, setShowComments] = useState(false);

  // Open comments panel automatically when deep-linked
  useEffect(() => {
    if (defaultShowComments && isOpen) {
      setShowComments(true);
    }
  }, [defaultShowComments, isOpen]);

  useEffect(() => {
    if (embarque?.numero_do) {
      setEditedTitle(embarque.numero_do);
    }
    setIsEditingTitle(false);
    setShowDeleteConfirm(false);
  }, [embarque?.id]);

  const handleUpdateTitle = async () => {
    if (!embarque || !editedTitle.trim() || editedTitle.trim() === embarque.numero_do) {
      setIsEditingTitle(false);
      setEditedTitle(embarque?.numero_do ?? "");
      return;
    }
    const trimmed = editedTitle.trim();
    // Optimistic update
    setIsEditingTitle(false);
    queryClient.setQueryData<Embarque[]>(["embarques", orgId], (old) =>
      old?.map(e => e.id === embarque.id ? { ...e, numero_do: trimmed } : e)
    );
    const { error } = await supabase.from("embarques").update({ numero_do: trimmed }).eq("id", embarque.id);
    if (error) {
      // Rollback
      queryClient.setQueryData<Embarque[]>(["embarques", orgId], (old) =>
        old?.map(e => e.id === embarque.id ? { ...e, numero_do: embarque.numero_do } : e)
      );
      toast.error("Error al renombrar", { description: error.message });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["embarques", orgId] });
    toast.success("Referencia de embarque actualizada");
  };

  const handleDeleteEmbarque = async () => {
    if (!embarque) return;
    // Usa el RPC seguro: bloquea si hay documentos activos y desliga
    // los archivados (preserva la cadena inmutable de audit_logs).
    const { data, error } = await supabase.rpc("delete_embarque_safe", {
      p_embarque_id: embarque.id,
    });
    if (error) {
      toast.error("Error al eliminar", { description: error.message });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["embarques"] });
    handleClose();
    const desligados = (data as any)?.docs_archivados_desligados ?? 0;
    toast.success("Embarque eliminado", {
      description: desligados > 0
        ? `${desligados} documento(s) archivado(s) desligado(s) para preservar la auditoría.`
        : undefined,
    });
  };

  // TRM Aduana
  const [editingTrmAduana, setEditingTrmAduana] = useState(false);
  const [trmAduanaValue, setTrmAduanaValue] = useState("");
  const trmAduana = embarque?.trm_aduana ?? null;

  useEffect(() => {
    setTrmAduanaValue(trmAduana ? String(trmAduana) : "");
    setEditingTrmAduana(false);
  }, [trmAduana, embarque?.id]);

  const saveTrmAduana = async () => {
    if (!embarque) return;
    const val = parseFloat(trmAduanaValue) || null;
    await supabase.from("embarques").update({ trm_aduana: val }).eq("id", embarque.id);
    queryClient.invalidateQueries({ queryKey: ["embarques"] });
    setEditingTrmAduana(false);
    toast.success("TRM Aduana actualizada");
  };

  // Cross-filter state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [filteredDocIds, setFilteredDocIds] = useState<string[] | null>(null);

  const selectedDocumentType = useMemo(() => {
    if (!selectedDocumentId) return undefined;
    const doc = documents.find((d: any) => d.id === selectedDocumentId);
    return doc?.tipo_documento || undefined;
  }, [selectedDocumentId, documents]);

  useEffect(() => {
    setSelectedDocumentId(null);
    setFilteredDocIds(null);
    setShowAddGasto(false);
  }, [embarque?.id]);

  const handleFilteredIdsChange = useCallback((ids: string[]) => {
    setFilteredDocIds(ids);
  }, []);

  // Deep-link interno desde banner de discrepancia → tab Documentos + highlight.
  const handleVerifyWeightDocs = useCallback((ids: string[]) => {
    setInnerTab("docs-soporte");
    setHighlightDocIds(ids);
    // Auto-clear: 3.5s es suficiente para que el ojo registre el pulso ámbar
    // sin dejar UI "sucia" si el usuario decide ignorar la sugerencia.
    window.setTimeout(() => setHighlightDocIds([]), 3500);
  }, []);

  // Reset highlight cuando cambia de embarque.
  useEffect(() => {
    setHighlightDocIds([]);
    setInnerTab("docs-soporte");
  }, [embarque?.id]);

  // COGS filter
  const cogsDocIds = useMemo(() => {
    const COGS_TYPES = ["factura internacional", "factura de compras"];
    return new Set(
      documents
        .filter((d: any) => COGS_TYPES.includes((d.tipo_documento ?? "").toLowerCase().trim()))
        .map((d: any) => d.id)
    );
  }, [documents]);

  const allEmbarqueInventory = useMemo(() => {
    return inventory.filter(i => cogsDocIds.has(i.documento_id));
  }, [inventory, cogsDocIds]);

  const filteredInventory = useMemo(() => {
    let result = [...allEmbarqueInventory];
    if (filteredDocIds !== null) {
      const filteredSet = new Set(filteredDocIds);
      result = result.filter(item => filteredSet.has(item.documento_id));
    }
    if (selectedDocumentId) {
      result = result.filter(item => item.documento_id === selectedDocumentId);
    }
    return result;
  }, [allEmbarqueInventory, filteredDocIds, selectedDocumentId]);

  const hsCodesKey = useMemo(() => {
    return allEmbarqueInventory.map(i => i.hs_code ?? "").join("|");
  }, [allEmbarqueInventory]);

  useEffect(() => {
    if (!allEmbarqueInventory.length) return;
    allEmbarqueInventory.forEach((item, idx) => {
      const code = (item.hs_code ?? "").trim();
      if (code) complianceLookup(idx, code);
    });
  }, [hsCodesKey, complianceLookup]);

  // Si el embarque tiene TRM Aduana (vino en la DIM), se asume como tasa
  // oficial para conversión de gastos en USD — evita desfases entre la TRM
  // de mercado y la declarada ante la DIAN.
  const effectiveTrmMap: TRMMap = useMemo(() => {
    const base: TRMMap = { COP: 1, ...trmMap };
    if (trmAduana && trmAduana > 0) base.USD = trmAduana;
    return base;
  }, [trmMap, trmAduana]);

  const trmLockedByAduana = !!(trmAduana && trmAduana > 0);
  const hasTRM = (effectiveTrmMap.USD ?? 0) > 0;

  // ── ENG-01 Round 3: matriz de beneficio aduanero por ítem ──────────────
  // El servidor resuelve arancel/IVA desde arancel_1881 (vía hs_code) y
  // aplica la matriz de regímenes. Solo necesitamos enviarle el régimen
  // efectivo (con back-compat al flag legacy `aplica_tlc`).
  const beneficioByItemId = useMemo(() => {
    const map: Record<string, "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA"> = {};
    allEmbarqueInventory.forEach((item) => {
      const explicit = (item as any).beneficio_aduanero as
        | "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA" | null | undefined;
      if (explicit && explicit !== "GENERAL") {
        map[item.id] = explicit;
      } else if (explicit === "GENERAL") {
        map[item.id] = "GENERAL";
      } else {
        map[item.id] = item.aplica_tlc ? "TLC_PURO" : "GENERAL";
      }
    });
    return map;
  }, [allEmbarqueInventory]);

  // Pasar gastos completos (incluye trm_pago y aplica_costeo_inventario).
  // CRÍTICO: enviamos `monto` (Total con IVA) Y `monto_base` (sin IVA).
  // El hook async usa `monto_base` para que el motor capitalice únicamente la base
  // (regla contable: IVA no entra a inventario).
  const gastosForEngine: ShipmentGasto[] = useMemo(() => {
    return gastos.map(g => ({
      id: g.id,
      concepto: g.concepto,
      monto: g.monto,
      monto_base: g.monto_base,
      moneda: g.moneda,
      base_prorrateo: g.base_prorrateo as BaseProrrateo,
      trm_pago: g.trm_pago,
      aplica_costeo_inventario: g.aplica_costeo_inventario,
      incluir_en_base_gravable: g.incluir_en_base_gravable,
    }));
  }, [gastos]);

  // ── Hook async server-driven (RPC calculate_landed_cost_preview) ────────
  // Reemplaza `calcShipmentLandedCost` (deprecated). Mantiene `ShipmentLandedResult`.
  const {
    data: shipmentLC,
    isLoading: shipmentLCLoading,
    isFetching: shipmentLCFetching,
    error: shipmentLCError,
  } = useShipmentLandedCost({
    documents: documents as any,
    items: allEmbarqueInventory as any,
    gastos: gastosForEngine,
    trmAduana,
    beneficioByItemId,
    enabled: allEmbarqueInventory.length > 0 && documents.length > 0,
  });

  const totalLandedCOP = shipmentLC?.totalLandedCOP ?? 0;
  const totalFOB_COP = shipmentLC?.globals.fobTotalCOP ?? 0;
  // IVA total agregado (suma per-item, no doc-level legacy).
  const ivaTotalCOP = useMemo(
    () => (shipmentLC?.items ?? []).reduce((acc, r) => acc + (r.ivaItemCOP || 0), 0),
    [shipmentLC],
  );

  // ── Reconciliación de pesos (cabecera vs items) ──
  // Detecta GIGO en captura de peso para evitar prorrateo logístico erróneo.
  const weightRecon = useMemo(
    () =>
      computeWeightReconciliation({
        embarqueOverrideKg: embarque?.peso_bruto_kg,
        documents: documents as any,
        items: allEmbarqueInventory as any,
      }),
    [embarque?.peso_bruto_kg, documents, allEmbarqueInventory],
  );

  // BUG-FIX TRAZABILIDAD: el pool por Valor (`seguroAsignado` legacy) agrega
  // SEGURO + ENDOSO + MANEJO + otros. Para el recibo de trazabilidad debemos
  // separar el seguro internacional real del resto. Calculamos un ratio
  // (seguro / total Valor capitalizable) usando los gastos del embarque y
  // dividimos `allocValor` proporcionalmente. Si no hay gasto de seguro, el
  // ratio es 0 y todo cae en "otros gastos por Valor".
  const seguroValorRatio = useMemo(() => {
    const isSeguro = (g: GastoEmbarque) => {
      if (g.aplica_costeo_inventario === false) return false;
      if (g.base_prorrateo !== "Valor") return false;
      const cat = (g as any).categoria_id ?? "";
      if (typeof cat === "string" && cat.toUpperCase() === "SEGURO_INT") return true;
      const concepto = (g.concepto ?? "").toLowerCase();
      return concepto.includes("seguro");
    };
    let totalValor = 0;
    let totalSeguro = 0;
    for (const g of gastos) {
      if (g.aplica_costeo_inventario === false) continue;
      if (g.base_prorrateo !== "Valor") continue;
      const capitalizable = Number(g.monto_base ?? g.monto ?? 0);
      if (!Number.isFinite(capitalizable) || capitalizable <= 0) continue;
      totalValor += capitalizable;
      if (isSeguro(g)) totalSeguro += capitalizable;
    }
    return totalValor > 0 ? totalSeguro / totalValor : 0;
  }, [gastos]);

  const landedCostByItemId = useMemo(() => {
    if (!shipmentLC) return {};
    const map: Record<string, {
      landedCostUnitario: number;
      landedCostUnitarioCOP: number | null;
      fobItemCOP?: number;
      fleteAsignado?: number;
      /** @deprecated alias del pool por Valor agregado. Usar seguroIntAsignado + otrosValorAsignados. */
      seguroAsignado?: number;
      seguroIntAsignado?: number;
      otrosValorAsignados?: number;
      otrosAsignados?: number;
      arancelItemCOP?: number;
      ivaItemCOP?: number;
      arancelPctEfectivo?: number;
      ivaPctEfectivo?: number;
      landedCostTotalCOP?: number;
      monedaOrigen?: string;
    }> = {};
    shipmentLC.items.forEach(r => {
      const allocValor = r.seguroAsignado;
      const seguroIntAsignado = allocValor * seguroValorRatio;
      const otrosValorAsignados = allocValor - seguroIntAsignado;
      map[r.itemId] = {
        landedCostUnitario: r.landedCostUnitarioCOP,
        landedCostUnitarioCOP: r.landedCostUnitarioCOP,
        // FOB ítem = CIF − (pool Peso + pool Valor) — derivado del v2.
        fobItemCOP: r.cifItemCOP - r.gastosPesoAsignados - r.gastosValorAsignados,
        fleteAsignado: r.fleteAsignado,
        seguroAsignado: allocValor, // legacy — mantener para no romper InventoryTable
        seguroIntAsignado,
        otrosValorAsignados,
        otrosAsignados: r.otrosAsignados,
        arancelItemCOP: r.arancelItemCOP,
        ivaItemCOP: r.ivaItemCOP,
        arancelPctEfectivo: r.arancelPctEfectivo,
        ivaPctEfectivo: r.ivaPctEfectivo,
        landedCostTotalCOP: r.landedCostTotalCOP,
        monedaOrigen: r.monedaOrigen,
      };
    });
    return map;
  }, [shipmentLC, seguroValorRatio]);

  // TSK-207: Effective peso/volumen — embarque override > docs max.
  // The shipment-level value is authoritative when defined (>0), enabling
  // accurate logistics proration even when invoices omit physical data.
  const { totalPeso, totalVolumen, pesoSource } = useMemo(() => {
    const pesos = documents.map((doc: any) => parseFloat(String(doc.peso_bruto_kg)) || 0);
    const volumenes = documents.map((doc: any) => parseFloat(String(doc.volumen_cbm)) || 0);
    const docPesoMax = pesos.length ? Math.max(...pesos) : 0;
    const docVolMax = volumenes.length ? Math.max(...volumenes) : 0;
    const ovrPeso = Number(embarque?.peso_bruto_kg ?? 0) || 0;
    const ovrVol = Number(embarque?.volumen_cbm ?? 0) || 0;
    return {
      totalPeso: ovrPeso > 0 ? ovrPeso : docPesoMax,
      totalVolumen: ovrVol > 0 ? ovrVol : docVolMax,
      pesoSource: ovrPeso > 0 ? "override" as const : "docs" as const,
    };
  }, [documents, embarque?.peso_bruto_kg, embarque?.volumen_cbm]);

  const [editingETA, setEditingETA] = useState(false);
  const [etaValue, setEtaValue] = useState("");

  useEffect(() => {
    setEtaValue(embarque?.fecha_llegada_estimada ?? "");
    setEditingETA(false);
  }, [embarque?.id]);

  // TSK-207: Inline edit state for peso/volumen
  const [editingPeso, setEditingPeso] = useState(false);
  const [pesoValue, setPesoValue] = useState("");
  const [volumenValue, setVolumenValue] = useState("");
  const [savingPeso, setSavingPeso] = useState(false);

  useEffect(() => {
    setPesoValue(String(embarque?.peso_bruto_kg ?? ""));
    setVolumenValue(String(embarque?.volumen_cbm ?? ""));
    setEditingPeso(false);
  }, [embarque?.id]);

  const saveETA = async () => {
    if (!embarque) return;
    await supabase.from("embarques").update({ fecha_llegada_estimada: etaValue || null }).eq("id", embarque.id);
    queryClient.invalidateQueries({ queryKey: ["embarques"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-documents", embarque.id] });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["all-documents"] });
    setEditingETA(false);
  };

  // TSK-207: Save peso/volumen overrides at the shipment level
  const savePesoVolumen = async () => {
    if (!embarque) return;
    const peso = pesoValue.trim() === "" ? 0 : parseFloat(pesoValue);
    const vol = volumenValue.trim() === "" ? 0 : parseFloat(volumenValue);
    if (Number.isNaN(peso) || peso < 0 || Number.isNaN(vol) || vol < 0) {
      toast.error("Valores inválidos", { description: "Peso y volumen deben ser números ≥ 0." });
      return;
    }
    setSavingPeso(true);
    // Optimistic update
    queryClient.setQueryData<Embarque[]>(["embarques", orgId], (old) =>
      old?.map(e => e.id === embarque.id ? { ...e, peso_bruto_kg: peso, volumen_cbm: vol } : e)
    );
    const { error } = await supabase
      .from("embarques")
      .update({ peso_bruto_kg: peso, volumen_cbm: vol })
      .eq("id", embarque.id);
    setSavingPeso(false);
    if (error) {
      // Rollback by invalidating
      queryClient.invalidateQueries({ queryKey: ["embarques"] });
      toast.error("Error al guardar peso/volumen", { description: error.message });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["embarques"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-documents", embarque.id] });
    setEditingPeso(false);
    toast.success("Peso y volumen actualizados", {
      description: peso > 0 ? "El motor de Landed Cost recalculará el prorrateo." : "Sin peso, los gastos por Peso usarán Valor.",
    });
  };

  const presentDocTypes = useMemo(() => {
    const types = new Set(documents.map((d: any) => (d.tipo_documento ?? "").toLowerCase().trim()).filter(Boolean));
    return types;
  }, [documents]);

  useEffect(() => {
    if (!embarque) return;
    const prov = embarque.proveedor?.trim();
    if (prov && prov !== "Pendiente") return;
    const invoice = documents.find((d: any) =>
      d.tipo_documento === "Factura Internacional" || d.tipo_documento === "Factura de Compras"
    );
    if (invoice && (invoice as any).emisor) {
      supabase.from("embarques").update({ proveedor: (invoice as any).emisor }).eq("id", embarque.id).then(() => {
        queryClient.invalidateQueries({ queryKey: ["embarques"] });
      });
    }
  }, [embarque?.id, embarque?.proveedor, documents]);

  const typedDocuments: DocumentRecord[] = useMemo(() => {
    return documents.map((d: any) => ({
      id: d.id,
      created_at: d.created_at,
      id_correo: d.id_correo || "",
      fecha: d.fecha || "",
      tipo_documento: d.tipo_documento || "",
      no_documento: d.no_documento,
      emisor: d.emisor || "",
      receptor: d.receptor || "",
      moneda: d.moneda || "",
      total_factura: d.total_factura ?? 0,
      costo_flete: d.costo_flete ?? 0,
      seguro: d.seguro ?? 0,
      impuestos: d.impuestos ?? 0,
      descuento_ajuste: d.descuento_ajuste ?? 0,
      otros_cargos: d.otros_cargos ?? 0,
      origen: d.origen || "",
      destino: d.destino || "",
      incoterm: d.incoterm || "",
      notas_tecnicas: d.notas_tecnicas || "",
      archivo_url: d.archivo_url || null,
      estado: d.estado || "Pendiente",
      peso_bruto_kg: d.peso_bruto_kg || 0,
      peso_neto_kg: d.peso_neto_kg || 0,
      volumen_cbm: d.volumen_cbm || 0,
      total_piezas: d.total_piezas || 0,
      embarque_id: d.embarque_id || null,
      embarque_numero_do: embarque?.numero_do || null,
      datos_extra: d.datos_extra || null,
    })) as DocumentRecord[];
  }, [documents, embarque?.numero_do]);

  // TSK-206: Lookup map for source document number per gasto
  const docIdToNumero = useMemo(() => {
    const m = new Map<string, string>();
    documents.forEach((d: any) => {
      if (d?.id) m.set(d.id, d.no_documento ?? "");
    });
    return m;
  }, [documents]);

  // ── Modo de transporte (inline editor) ──
  const saveModoTransporte = async (modo: ModoTransporte | null) => {
    if (!embarque) return;
    const prev = (embarque as any).modo_transporte ?? null;
    if (modo === prev) return;
    // Optimistic
    queryClient.setQueryData<Embarque[]>(["embarques", orgId], (old) =>
      old?.map(e => e.id === embarque.id ? ({ ...e, modo_transporte: modo } as any) : e)
    );
    const { error } = await supabase
      .from("embarques")
      .update({ modo_transporte: modo } as any)
      .eq("id", embarque.id);
    if (error) {
      queryClient.setQueryData<Embarque[]>(["embarques", orgId], (old) =>
        old?.map(e => e.id === embarque.id ? ({ ...e, modo_transporte: prev } as any) : e)
      );
      toast.error("Error al guardar modo de transporte", { description: error.message });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["embarques", orgId] });
    toast.success(`Modo de transporte: ${modo ?? "Sin definir"}`);
  };

  const handleLiquidar = async (excepcion?: ExcepcionPayload | null) => {
    if (!embarque) return;
    try {
      // Build the immutable snapshot the backend will persist into
      // `landed_cost_snapshots`. We send the *current preview* so auditors
      // can later replay exactly what the user saw at freeze time.
      const snapshot = {
        engine_version: "v2",
        committed_at_client: new Date().toISOString(),
        embarque: {
          id: embarque.id,
          numero_do: embarque.numero_do,
          trm_aduana: trmAduana,
          modo_transporte: (embarque as any)?.modo_transporte ?? null,
          peso_total_kg: totalPeso,
          fob_total_cop: totalFOB_COP,
        },
        totales: {
          fob_cop: totalFOB_COP,
          landed_cop: totalLandedCOP,
          iva_cop: ivaTotalCOP,
        },
        items: shipmentLC?.items ?? [],
        gastos: gastos.map((g) => {
          const montoBase = Number(g.monto_base ?? 0);
          const montoTotal = Number(g.monto ?? 0);
          const montoCapitalizable = montoBase > 0 ? montoBase : montoTotal;
          const impuestos = Number(g.impuestos_locales ?? 0);
          return {
            id: g.id,
            concepto: g.concepto,
            // Desglose financiero explícito para auditoría (Pagado vs Capitalizado):
            monto_total: montoTotal,           // Lo que efectivamente se pagó (Base + IVA)
            monto_base: montoBase,             // Subtotal sin IVA (fuente: OCR)
            impuestos_locales: impuestos,      // IVA descontable — NO capitaliza
            monto_capitalizable: montoCapitalizable, // Lo que entró al motor de Landed Cost
            // Compat. legacy (snapshots previos esperan `monto`):
            monto: montoCapitalizable,
            moneda: g.moneda,
            base_prorrateo: g.base_prorrateo,
            aplica_costeo_inventario: g.aplica_costeo_inventario,
            fuente: (g as any).fuente ?? null,
            categoria_id: (g as any).categoria_id ?? null,
            grupo_conflicto_id: (g as any).grupo_conflicto_id ?? null,
          };
        }),
      };

      const result = await freezeMutation.mutateAsync({
        embarqueId: embarque.id,
        snapshot,
        excepcion: excepcion ?? null,
      });
      toast.success(excepcion ? "Liquidación congelada con excepción firmada" : "Liquidación congelada", {
        description: result?.message ?? `Snapshot inmutable guardado para auditoría.`,
      });
      setShowExcepcionDialog(false);
    } catch (err: any) {
      toast.error("Error al congelar liquidación", { description: err.message });
    }
  };

  const isLiquidado = embarque?.liquidado === true;
  const isNacionalizado = embarque?.estado_logistico === "Nacionalizado";
  const isOrphan = !((embarque as any)?.cliente_id);

  // ── TSK-Compliance: liquidation guard (composable validators) ──
  // FOB total in USD = COP / TRM Aduana (used by the conditional DAV rule).
  const fobTotalUSD = useMemo(() => {
    if (!totalFOB_COP || !trmAduana || trmAduana <= 0) return 0;
    return totalFOB_COP / trmAduana;
  }, [totalFOB_COP, trmAduana]);

  // Detect whether the active DIM document already has both numero_dim & numero_levante.
  // Drives the "Datos de Nacionalización" warning rule in the compliance engine.
  const dimDataComplete = useMemo(() => {
    const dim = (documents as any[]).find(
      (d) => normaliseTipo(d.tipo_documento) === normaliseTipo("Declaración de Importación"),
    );
    return !!(dim?.numero_dim && dim?.numero_levante);
  }, [documents]);

  const liquidationGuard = useLiquidationGuard({
    orgId,
    isLiquidado,
    trmAduana,
    clienteId: (embarque as any)?.cliente_id ?? null,
    totalPesoKg: totalPeso,
    modoTransporte: ((embarque as any)?.modo_transporte ?? null) as ModoTransporte | null,
    fobTotalUSD,
    dimDataComplete,
    documents: documents as any,
    // Pasamos TODO el inventario (no sólo COGS) para que la Capa 2 (integridad
    // cruzada) pueda comparar HS de Factura ↔ BL ↔ Packing ↔ DIM ↔ Cert. Origen.
    items: inventory as any,
  });

  /** Estado del AuditPanel embebido (deep-link desde CompliancePill). */
  const [auditDocId, setAuditDocId] = useState<string | null>(null);
  const auditDoc = useMemo<DocumentRecord | null>(() => {
    if (!auditDocId) return null;
    return typedDocuments.find((d) => d.id === auditDocId) ?? null;
  }, [auditDocId, typedDocuments]);

  /** Deep-link handler: abre el AuditPanel del documento Y resalta su fila. */
  const handleOpenDocumentAudit = useCallback((documentId: string) => {
    setSelectedDocumentId(documentId);
    setInnerTab("docs-soporte");
    setHighlightDocIds([documentId]);
    setAuditDocId(documentId);
  }, []);

  if (!embarque) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[95vw] w-full max-h-[95vh] overflow-hidden flex flex-col z-[50]">
        <DialogHeader className="flex flex-row items-center justify-between gap-2 pr-10">
          <DialogTitle className="flex items-center gap-2 min-w-0">
            <Ship className="h-5 w-5 text-primary shrink-0" />
            {isEditingTitle ? (
              <Input
                autoFocus
                value={editedTitle}
                onChange={e => setEditedTitle(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleUpdateTitle(); if (e.key === "Escape") { setIsEditingTitle(false); setEditedTitle(embarque.numero_do); } }}
                onBlur={handleUpdateTitle}
                className="h-7 text-base font-semibold w-48"
              />
            ) : (
              <span className="flex items-center gap-1.5 group cursor-pointer" onClick={() => setIsEditingTitle(true)}>
                Embarque: {editedTitle || embarque.numero_do}
                <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
            )}
          </DialogTitle>

          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setShowComments(true)} title="Comentarios">
              <MessageSquare className="h-4 w-4" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive focus:bg-destructive/10 gap-2"
                  onClick={() => {
                    if (documents.length > 0) {
                      toast.error("No se puede eliminar un embarque con documentos asociados.", {
                        description: "Elimina los documentos primero.",
                      });
                      return;
                    }
                    setShowDeleteConfirm(true);
                  }}
                  disabled={isLiquidado}
                >
                  <Trash2 className="h-4 w-4" />
                  Eliminar Embarque
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar embarque?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción eliminará permanentemente el embarque <strong>{embarque.numero_do}</strong>. Esta operación no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteEmbarque} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Badge variant="outline">{embarque.estado_logistico}</Badge>
          <DossierSignatureBadge embarqueId={embarque.id} />
          {trmAduana && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="text-xs cursor-help border border-primary/30">
                    TRM Aduana: {copFormatter.format(trmAduana)} 🔒
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs max-w-[280px]">
                  Tasa fijada por la TRM de la Declaración de Importación. Se aplica automáticamente a los gastos en USD para evitar desfases con la liquidación DIAN.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Modo de transporte — habilita reglas condicionales del compliance (BL vs Guía Aérea) */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground">Transporte:</span>
            <Select
              value={(embarque as any).modo_transporte ?? "__none__"}
              onValueChange={(v) => saveModoTransporte(v === "__none__" ? null : (v as ModoTransporte))}
              disabled={isLiquidado}
            >
              <SelectTrigger className={`h-7 w-[124px] text-[11px] ${!(embarque as any).modo_transporte ? "border-amber-500/60 text-amber-700 dark:text-amber-400" : ""}`}>
                <SelectValue placeholder="⚠️ Definir" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="text-xs italic text-muted-foreground">Sin definir</SelectItem>
                {MODOS_TRANSPORTE.map((m) => (
                  <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>


          {/* Proveedor selector — inline editable, mirrors Cliente pattern */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">Proveedor:</span>
            <div className="w-56">
              <ProveedorSelectField
                value={(embarque as any).proveedor_id ?? ""}
                invalid={!(embarque as any).proveedor_id}
                placeholder="⚠️ Sin proveedor — asignar"
                defaultSearch={(embarque as any).proveedor_id ? "" : (embarque.proveedor ?? "")}
                onValueChange={async (newId, payload) => {
                  const prevId = (embarque as any).proveedor_id ?? null;
                  const prevName = embarque.proveedor ?? null;
                  if (newId === prevId) return;
                  const newName = payload?.nombre ?? null;

                  // Optimistic — sync proveedor_id + snapshot proveedor (text)
                  queryClient.setQueryData<Embarque[]>(["embarques", orgId], (old) =>
                    old?.map(e => e.id === embarque.id
                      ? { ...e, proveedor_id: newId, proveedor: newName } as any
                      : e)
                  );

                  const { error } = await supabase
                    .from("embarques")
                    .update({ proveedor_id: newId || null, proveedor: newName } as any)
                    .eq("id", embarque.id);

                  if (error) {
                    // Rollback
                    queryClient.setQueryData<Embarque[]>(["embarques", orgId], (old) =>
                      old?.map(e => e.id === embarque.id
                        ? { ...e, proveedor_id: prevId, proveedor: prevName } as any
                        : e)
                    );
                    toast.error("Error al asignar proveedor", { description: error.message });
                    return;
                  }

                  // ── Aprendizaje en cascada de alias de proveedor ──
                  // Si había un snapshot OCR previo distinto al nombre vinculado,
                  // o si algún documento del embarque tiene un emisor diferente,
                  // guarda esa variación como alias para auto-vincular en el futuro.
                  if (newId) {
                    const orgId = (embarque as any).org_id;
                    const candidates = new Set<string>();
                    if (prevName && prevName.trim() && prevName !== newName) {
                      candidates.add(prevName.trim());
                    }
                    documents.forEach((d: any) => {
                      const em = (d.emisor || "").trim();
                      if (em && em !== newName) candidates.add(em);
                    });

                    for (const ocrText of candidates) {
                      if (!orgId) break;
                      await supabase
                        .from("proveedor_aliases")
                        .insert({ proveedor_id: newId, nombre_ocr: ocrText, org_id: orgId })
                        .then(({ error: aliasErr }) => {
                          if (aliasErr && !aliasErr.message.toLowerCase().includes("duplicate")) {
                            console.warn("No se pudo guardar alias de proveedor:", aliasErr.message);
                          } else if (!aliasErr) {
                            console.log(`📚 Alias aprendido: "${ocrText}" → proveedor ${newId}`);
                          }
                        });
                    }
                  }

                  queryClient.invalidateQueries({ queryKey: ["embarques", orgId] });
                  queryClient.invalidateQueries({ queryKey: ["embarque-documents", orgId, embarque.id] });
                  queryClient.invalidateQueries({ queryKey: ["documents"] });
                  queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
                  queryClient.invalidateQueries({ queryKey: ["proveedores"] });
                  toast.success("Proveedor asignado al embarque");
                }}
              />
            </div>
          </div>

          {/* Cliente selector — inline editable. Critical for B2B portal visibility. */}
          <div className="flex items-center gap-2 ml-auto">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">Cliente:</span>
            <div className="w-56">
              <ClienteSelectField
                value={(embarque as any).cliente_id ?? ""}
                invalid={!(embarque as any).cliente_id}
                placeholder="⚠️ Sin cliente — asignar"
                onValueChange={async (newId) => {
                  const prev = (embarque as any).cliente_id ?? null;
                  if (newId === prev) return;
                  // Optimistic
                  queryClient.setQueryData<Embarque[]>(["embarques", orgId], (old) =>
                    old?.map(e => e.id === embarque.id ? { ...e, cliente_id: newId } as any : e)
                  );
                  const { error } = await supabase
                    .from("embarques")
                    .update({ cliente_id: newId } as any)
                    .eq("id", embarque.id);
                  if (error) {
                    queryClient.setQueryData<Embarque[]>(["embarques", orgId], (old) =>
                      old?.map(e => e.id === embarque.id ? { ...e, cliente_id: prev } as any : e)
                    );
                    toast.error("Error al asignar cliente", { description: error.message });
                    return;
                  }

                  // ── Frente C: aprendizaje en cascada ──
                  // Guarda el receptor del primer documento como alias del cliente
                  // para que el próximo PDF con el mismo "Bill To" se auto-vincule.
                  if (newId) {
                    const receptorOcr = documents
                      .map((d: any) => (d.receptor || "").trim())
                      .find((r: string) => r.length > 0);
                    const orgId = (embarque as any).org_id;
                    if (receptorOcr && orgId) {
                      await supabase
                        .from("cliente_aliases")
                        .insert({ cliente_id: newId, nombre_ocr: receptorOcr, org_id: orgId })
                        .then(({ error: aliasErr }) => {
                          if (aliasErr && !aliasErr.message.toLowerCase().includes("duplicate")) {
                            console.warn("No se pudo guardar alias de cliente:", aliasErr.message);
                          } else if (!aliasErr) {
                            console.log(`📚 Alias aprendido: "${receptorOcr}" → cliente ${newId}`);
                          }
                        });
                    }
                  }

                  queryClient.invalidateQueries({ queryKey: ["embarques", orgId] });
                  queryClient.invalidateQueries({ queryKey: ["embarque-documents", orgId, embarque.id] });
                  toast.success("Cliente asignado al embarque");
                }}
              />
            </div>
          </div>
        </div>

        {/* Risk Radar Nivel 0: Embarque huérfano — bloqueante */}
        {!(embarque as any).cliente_id && (
          <Alert variant="destructive" className="border-destructive/60 bg-destructive/10 mt-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="font-semibold">🔴 Embarque Huérfano — Sin Cliente Asignado</AlertTitle>
            <AlertDescription className="text-[12px] leading-snug">
              Este embarque <strong>no tiene un Cliente importador asignado</strong>, por lo que es <strong>invisible en el Portal B2B</strong> y no puede liquidarse. Asigne un cliente inmediatamente desde el selector arriba.
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="expediente" className="flex-1 flex flex-col overflow-hidden mt-2">
          <TabsList className="shrink-0">
            <TabsTrigger value="expediente" className="gap-1">
              <FileText className="h-3.5 w-3.5" />
              Expediente
            </TabsTrigger>
            <TabsTrigger value="aduanas" className="gap-1">
              <Lock className="h-3.5 w-3.5" />
              Aduanas
            </TabsTrigger>
            <TabsTrigger value="tracking" className="gap-1">
              <MapPin className="h-3.5 w-3.5" />
              Tracking {quota.planType !== 'ENTERPRISE' && '🔒'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="expediente" className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
            <div className="space-y-4 pb-10">
              {/* === ENG-01 Round 3: estado del motor de Landed Cost server-side === */}
              {shipmentLCError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>No se pudo calcular el Landed Cost</AlertTitle>
                  <AlertDescription>{shipmentLCError.message}</AlertDescription>
                </Alert>
              )}
              {/* === KPI Ribbon (Nivel 1) === */}
              <div className={cn(
                "flex flex-row items-center h-14 bg-muted/20 border border-border rounded-lg px-4 w-full gap-0 overflow-x-auto scrollbar-hide transition-opacity",
                (shipmentLCFetching || shipmentLCLoading) && "opacity-60",
              )}>
                {/* FOB */}
                <div className="flex flex-col min-w-0 shrink-0">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">FOB (COP)</span>
                  <span className="text-sm font-semibold tabular-nums text-foreground">{hasTRM ? copFormatter.format(totalFOB_COP) : "Sin TRM"}</span>
                </div>
                <Separator orientation="vertical" className="h-8 mx-4" />
                {/* Landed Cost */}
                <div className="flex flex-col min-w-0 shrink-0">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    Landed Cost (COP)
                    {(shipmentLCLoading || shipmentLCFetching) && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-label="Recalculando" />
                    )}
                    {shipmentLC && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              variant={shipmentLC.isEstimated ? "outline" : "default"}
                              className={cn(
                                "h-4 px-1.5 text-[9px] uppercase tracking-wider font-medium cursor-help",
                                shipmentLC.isEstimated
                                  ? "border-amber-500 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30"
                                  : "bg-emerald-600 text-white hover:bg-emerald-600"
                              )}
                            >
                              {shipmentLC.isEstimated ? "Estimado" : "Final"}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-xs">
                            {shipmentLC.isEstimated ? (
                              <p className="text-xs">
                                <strong>Cálculo estimado (sin DIM).</strong> Se está usando la TRM de mercado más reciente porque aún no se ha cargado la Declaración de Importación. El valor se actualizará automáticamente con la TRM oficial cuando llegue la DIM.
                              </p>
                            ) : (
                              <p className="text-xs">
                                <strong>Cálculo final (con DIM).</strong> Calculado con la TRM Aduanera oficial de la Declaración de Importación.
                              </p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={cn(
                          "text-sm font-semibold tabular-nums cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2",
                          shipmentLC?.isEstimated
                            ? "text-amber-700 dark:text-amber-400"
                            : "text-emerald-700 dark:text-emerald-400"
                        )}>
                          {shipmentLC ? copFormatter.format(totalLandedCOP) : (hasTRM ? copFormatter.format(totalLandedCOP) : "Sin TRM")}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" align="start" sideOffset={8} collisionPadding={16} className="w-[320px]">
                        <div className="space-y-1 text-[11px]">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Desglose financiero</p>
                          <div className="flex justify-between gap-4">
                            <span>Costo inventario (sin IVA)</span>
                            <span className="tabular-nums font-medium">{copFormatter.format(totalLandedCOP)}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span>IVA descontable (19%)</span>
                            <span className="tabular-nums font-medium">{copFormatter.format(ivaTotalCOP)}</span>
                          </div>
                          <div className="border-t border-border/40 pt-1 mt-1 flex justify-between gap-4 font-semibold">
                            <span>Total salida de caja</span>
                            <span className="tabular-nums">{copFormatter.format(totalLandedCOP + ivaTotalCOP)}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground italic leading-snug pt-1">
                            NIC 2: el IVA es recuperable y NO capitaliza al inventario.
                          </p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Separator orientation="vertical" className="h-8 mx-4" />
                {/* Peso / Volumen — TSK-207: inline editable */}
                <div className="flex flex-col min-w-0 shrink-0">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Peso / Vol</span>
                  {editingPeso ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={pesoValue}
                        onChange={e => setPesoValue(e.target.value)}
                        placeholder="kg"
                        className="h-6 text-xs w-20"
                        aria-label="Peso bruto en kg"
                      />
                      <span className="text-[10px] text-muted-foreground">kg</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={volumenValue}
                        onChange={e => setVolumenValue(e.target.value)}
                        placeholder="CBM"
                        className="h-6 text-xs w-20"
                        aria-label="Volumen en CBM"
                      />
                      <span className="text-[10px] text-muted-foreground">CBM</span>
                      <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={savePesoVolumen} disabled={savingPeso}>
                        {savingPeso ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={() => {
                        setPesoValue(String(embarque?.peso_bruto_kg ?? ""));
                        setVolumenValue(String(embarque?.volumen_cbm ?? ""));
                        setEditingPeso(false);
                      }}><X className="h-3 w-3" /></Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className={`text-sm font-semibold tabular-nums ${totalPeso > 0 ? "text-foreground" : "text-amber-600 dark:text-amber-400"}`}>
                        {totalPeso.toLocaleString("es-CO", { maximumFractionDigits: 1 })} kg · {totalVolumen.toLocaleString("es-CO", { maximumFractionDigits: 2 })} CBM
                      </span>
                      {pesoSource === "override" && (
                        <Badge variant="outline" className="h-4 text-[9px] px-1 border-primary/40 text-primary">manual</Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 shrink-0"
                        onClick={() => setEditingPeso(true)}
                        aria-label="Editar peso y volumen del embarque"
                      >
                        <Pencil className="h-2.5 w-2.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <Separator orientation="vertical" className="h-8 mx-4" />
                {/* ETA */}
                <div className="flex flex-col min-w-0 shrink-0">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">ETA</span>
                  {editingETA ? (
                    <div className="flex items-center gap-1">
                      <Input type="date" value={etaValue} onChange={e => setEtaValue(e.target.value)} className="h-6 text-xs w-28" />
                      <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={saveETA}><Check className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={() => setEditingETA(false)}><X className="h-3 w-3" /></Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold text-foreground">{embarque.fecha_llegada_estimada ? new Date(embarque.fecha_llegada_estimada).toLocaleDateString("es-CO") : "—"}</span>
                      <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={() => setEditingETA(true)}><Pencil className="h-2.5 w-2.5" /></Button>
                    </div>
                  )}
                </div>
                <Separator orientation="vertical" className="h-8 mx-4" />
                {/* TRM Aduana */}
                <div className="flex flex-col min-w-0 shrink-0">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">TRM Aduana</span>
                  {editingTrmAduana ? (
                    <div className="flex items-center gap-1">
                      <Input type="number" value={trmAduanaValue} onChange={e => setTrmAduanaValue(e.target.value)} placeholder="4200" className="h-6 text-xs w-20" />
                      <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={saveTrmAduana}><Check className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={() => setEditingTrmAduana(false)}><X className="h-3 w-3" /></Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold tabular-nums text-foreground">{trmAduana ? copFormatter.format(trmAduana) : "—"}</span>
                      {!isLiquidado && (
                        <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={() => setEditingTrmAduana(true)}><Pencil className="h-2.5 w-2.5" /></Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* === Bolsa de Gastos Globales (Nivel 2) === */}
              {weightRecon.hasMismatch && (
                <Alert className="border-amber-500/50 bg-amber-500/10 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                    Discrepancia de peso detectada ({weightRecon.deltaPct.toFixed(1)}%)
                  </AlertTitle>
                  <AlertDescription className="text-[11px] leading-snug text-amber-800/90 dark:text-amber-300/90">
                    <HoverCard openDelay={150} closeDelay={80}>
                      <HoverCardTrigger asChild>
                        <span className="cursor-help underline decoration-dotted decoration-amber-600/60 underline-offset-2">
                          Cabecera ({weightRecon.source === "override" ? "override embarque" : "suma de documentos"}):{" "}
                          <strong className="tabular-nums">{weightRecon.headerKg.toLocaleString("es-CO")} kg</strong>
                        </span>
                      </HoverCardTrigger>
                      <HoverCardContent align="start" className="w-80 p-0">
                        <div className="px-3 py-2 border-b border-border bg-muted/40">
                          <p className="text-xs font-semibold">Desglose de cabecera</p>
                          <p className="text-[10px] text-muted-foreground">
                            {weightRecon.source === "override"
                              ? "Peso autoritativo (override manual del embarque)"
                              : `${weightRecon.docsCalculados.length} documento(s) operativo(s)`}
                          </p>
                        </div>
                        {weightRecon.source === "override" ? (
                          <div className="px-3 py-3 text-[11px] text-muted-foreground">
                            El peso del embarque fue declarado manualmente y no proviene
                            de la suma de cabeceras. No hay desglose por documento.
                          </div>
                        ) : weightRecon.docsCalculados.length === 0 ? (
                          <div className="px-3 py-3 text-[11px] text-muted-foreground">
                            Sin documentos operativos con peso declarado.
                          </div>
                        ) : (
                          <>
                            <div className="max-h-56 overflow-y-auto divide-y divide-border">
                              {weightRecon.docsCalculados.map(d => (
                                <div key={d.id} className="flex items-baseline justify-between gap-3 px-3 py-1.5 text-[11px]">
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium truncate text-foreground" title={d.nombre}>
                                      {d.nombre}
                                    </p>
                                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                                      {d.tipo}
                                    </p>
                                  </div>
                                  <span className="tabular-nums font-semibold text-foreground shrink-0">
                                    {d.peso.toLocaleString("es-CO")} kg
                                  </span>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-baseline justify-between gap-3 px-3 py-2 border-t border-border bg-muted/40 text-[11px]">
                              <span className="font-semibold">Total cabecera</span>
                              <span className="tabular-nums font-semibold text-foreground">
                                {weightRecon.headerKg.toLocaleString("es-CO")} kg
                              </span>
                            </div>
                          </>
                        )}
                      </HoverCardContent>
                    </HoverCard>
                    {" · "}Suma de ítems:{" "}
                    <strong className="tabular-nums">{weightRecon.itemsKg.toLocaleString("es-CO")} kg</strong>
                    {" · "}Δ <strong className="tabular-nums">{weightRecon.deltaKg.toLocaleString("es-CO")} kg</strong>.
                    Tolerancia: {WEIGHT_RECONCILIATION_TOLERANCE_PCT}%. Revisa el peso de los ítems o el peso declarado del embarque
                    antes de liquidar — el prorrateo logístico puede estar sesgado.
                    {weightRecon.source === "documents" && weightRecon.docsCalculados.length > 0 && (
                      <div className="mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[11px] gap-1 border-amber-600/60 text-amber-800 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-950/40"
                          onClick={() => handleVerifyWeightDocs(weightRecon.docsCalculados.map(d => d.id))}
                        >
                          <FileSearch className="h-3 w-3" />
                          Verificar Documentos
                        </Button>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              {shipmentLC?.warningPesoIncompleto && (
                <Alert className="border-amber-500/50 bg-amber-500/10 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                    Peso parcial detectado — bolsa por Peso prorrateada por Valor FOB
                  </AlertTitle>
                  <AlertDescription className="text-[11px] leading-snug text-amber-800/90 dark:text-amber-300/90">
                    {shipmentLC.itemsSinPeso.length} ítem(s) sin <code>peso_bruto_kg</code> declarado.
                    Para no penalizar a los ítems con peso, toda la bolsa por Peso se prorratea por Valor FOB.
                    Declara el peso de los ítems faltantes para usar el prorrateo logístico real.
                  </AlertDescription>
                </Alert>
              )}
              <Card className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-muted-foreground">Bolsa de Gastos Globales (COP)</p>
                      {isLiquidado && (
                        <Badge variant="default" className="text-[10px] gap-1">
                          <Lock className="h-3 w-3" /> Liquidado
                        </Badge>
                      )}
                    </div>
                    {!isLiquidado && (
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setShowAddGasto(!showAddGasto)}>
                          <Plus className="h-3 w-3" /> Agregar Gasto
                        </Button>

                        {/* Compliance Pill — adjacent to Liquidar (Fitts' Law) */}
                        <CompliancePill guard={liquidationGuard} isLiquidado={isLiquidado} onOpenDocument={handleOpenDocumentAudit} />


                        {weightRecon.hasMismatch ? (
                          // Barrera 1: discrepancia → forzar firma de excepción
                          <Button
                            variant="default"
                            size="sm"
                            className="h-7 text-xs gap-1 bg-amber-600 hover:bg-amber-700 text-white"
                            disabled={!liquidationGuard.canLiquidate || freezeMutation.isPending || hasConflicts}
                            onClick={() => setShowExcepcionDialog(true)}
                            title="Discrepancia de peso detectada — requiere firma de excepción"
                          >
                            {freezeMutation.isPending
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <AlertTriangle className="h-3 w-3" />}
                            Liquidar con Excepción
                          </Button>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="default"
                                size="sm"
                                className="h-7 text-xs gap-1"
                                disabled={!liquidationGuard.canLiquidate || freezeMutation.isPending || hasConflicts}
                                title={hasConflicts ? "Resuelve los conflictos de gastos antes de congelar" : undefined}
                              >
                                {freezeMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}
                                Liquidar y Congelar Costos
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {liquidationGuard.summary.missingWarning > 0
                                    ? "⚠️ Congelar con advertencias"
                                    : "¿Congelar liquidación definitiva?"}
                                </AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div className="space-y-2">
                                    {liquidationGuard.blockers.length > 0 && (
                                      <div className="space-y-1.5">
                                        {liquidationGuard.blockers.map((b) => (
                                          <div
                                            key={b.id}
                                            className={`rounded-md border p-2.5 text-[11px] leading-snug ${
                                              b.severity === "critical"
                                                ? "border-destructive/50 bg-destructive/10 text-destructive"
                                                : "border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-300"
                                            }`}
                                          >
                                            <p className="text-xs font-semibold mb-0.5">{b.title}</p>
                                            <p>{b.message}</p>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <p className="text-sm">
                                      Esta acción es <strong>irreversible</strong>. Se calcularán y congelarán permanentemente el{" "}
                                      <strong>costo nacionalizado unitario</strong>, el <strong>arancel</strong> y el <strong>IVA</strong>{" "}
                                      de cada ítem usando la TRM Aduana de{" "}
                                      <strong>{trmAduana ? copFormatter.format(trmAduana) : "—"}</strong>.
                                      Se guardará un <strong>snapshot inmutable</strong> en el registro de auditoría aduanera
                                      y no se podrán editar gastos después.
                                    </p>
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleLiquidar(null)} disabled={!liquidationGuard.canLiquidate || hasConflicts}>
                                  {liquidationGuard.summary.missingWarning > 0
                                    ? "Congelar de todas formas"
                                    : "Confirmar y Congelar"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    )}
                    {isLiquidado && (
                      <CompliancePill guard={liquidationGuard} isLiquidado={isLiquidado} onOpenDocument={handleOpenDocumentAudit} />
                    )}
                  </div>

                  {showAddGasto && embarque && !isLiquidado && (
                    <div className="mb-3">
                      <AddGastoForm embarqueId={embarque.id} onDone={() => {}} />
                    </div>
                  )}

                  {/* TSK-207: Persistent zero-weight warning on the Gastos pool */}
                  {(!totalPeso || totalPeso === 0) && (
                    <Alert variant="default" className="mb-3 border-amber-500/50 bg-amber-500/10">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertTitle className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                        ⚠️ Peso no definido
                      </AlertTitle>
                      <AlertDescription className="text-[11px] text-amber-700/90 dark:text-amber-400/90">
                        Los gastos logísticos se están prorrateando por <strong>Valor</strong> como medida de emergencia.
                        Defina el peso para obtener un costo destino exacto.{" "}
                        <button
                          type="button"
                          onClick={() => setEditingPeso(true)}
                          className="underline font-semibold hover:text-amber-800 dark:hover:text-amber-300"
                        >
                          Definir peso ahora →
                        </button>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Phase 1 — Conflict resolver: lets the user dedupe gastos
                      grouped by `grupo_conflicto_id` (server-side detection). */}
                  <ExpenseConflictResolver embarqueId={embarque?.id ?? null} isLiquidado={isLiquidado} />

                  {/* Gastos from gastos_embarque table */}
                  {gastos.length > 0 && (
                    <div className="mb-3 space-y-1">
                      {gastos.map(g => {
                        const docNumero = g.documento_id ? docIdToNumero.get(g.documento_id) : null;
                        return (
                        <div key={g.id} className={`flex items-center justify-between text-sm px-2 py-1.5 rounded-md bg-muted/30 ${!g.aplica_costeo_inventario ? 'opacity-50' : ''}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            {/* TSK-206: Interactive base_prorrateo selector */}
                            {isLiquidado ? (
                              <Badge variant="outline" className="text-[10px] h-5">
                                {g.base_prorrateo}
                              </Badge>
                            ) : (
                              <Select
                                value={g.base_prorrateo}
                                onValueChange={(val: BaseProrrateo) => {
                                  if (val === g.base_prorrateo) return;
                                  updateGasto.mutate(
                                    { id: g.id, embarque_id: embarque!.id, base_prorrateo: val },
                                    {
                                      onSuccess: () => toast.success(`Base cambiada a ${val}. Recalculando…`),
                                      onError: (e: any) => toast.error(e?.message ?? "Error al actualizar base"),
                                    }
                                  );
                                }}
                              >
                                <SelectTrigger className="h-5 w-[88px] text-[10px] px-2 py-0 gap-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Peso" className="text-xs">Peso</SelectItem>
                                  <SelectItem value="Valor" className="text-xs">Valor</SelectItem>
                                  <SelectItem value="CBM" className="text-xs">CBM</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            <span className="text-foreground truncate">{g.concepto}</span>
                            {docNumero && (
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="secondary" className="text-[10px] h-5 gap-1 font-mono shrink-0">
                                      <FileText className="h-2.5 w-2.5" />
                                      {docNumero}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    Documento de origen: <span className="font-mono">{docNumero}</span>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {!g.aplica_costeo_inventario && (
                              <span className="text-[10px] text-muted-foreground italic">No prorratea</span>
                            )}
                          </div>
                          {(() => {
                            // TSK-208: Compute COP equivalent using trm_pago > trmMap fallback
                            const isManualTRM = !!(g.trm_pago && g.trm_pago > 0);
                            const rate = g.moneda === "COP"
                              ? 1
                              : (isManualTRM ? g.trm_pago! : effectiveTrmMap[g.moneda]);
                            const hasRate = typeof rate === "number" && rate > 0;
                            // Desglose: capitalizable (base sin IVA) vs total pagado.
                            const montoBase = Number(g.monto_base ?? 0);
                            const impuestos = Number(g.impuestos_locales ?? 0);
                            const montoTotal = Number(g.monto ?? 0);
                            const capitalizable = montoBase > 0 ? montoBase : montoTotal;
                            const hasBreakdown = montoBase > 0 && impuestos > 0;
                            const montoCOP = hasRate ? capitalizable * rate! : null;
                            const isAduanaLocked = !isManualTRM && trmLockedByAduana && g.moneda === "USD";
                            const rateOriginLabel = isManualTRM
                              ? "Origen: TRM de Pago Manual"
                              : isAduanaLocked
                                ? "Origen: TRM de la Declaración de Importación 🔒"
                                : "Origen: TRM Histórica del Sistema";
                            return (
                              <div className="flex items-center gap-2">
                                <div className="flex flex-col items-end leading-tight">
                                  <span className="font-medium text-foreground tabular-nums text-right inline-flex items-center gap-1.5 justify-end">
                                    <OcrBaseAlertIcon active={g.alerta_ocr_base} />
                                    {g.moneda === "COP" ? copFormatter.format(capitalizable) : `${g.moneda} ${capitalizable.toLocaleString()}`}
                                  </span>
                                  {/* Desglose Base + IVA (sutil) — solo si OCR detectó impuestos */}
                                  {hasBreakdown && (
                                    <TooltipProvider delayDuration={200}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="text-[10px] text-muted-foreground tabular-nums cursor-help">
                                            Base · +IVA {g.moneda === "COP" ? copFormatter.format(impuestos) : `${impuestos.toLocaleString()}`} · Pagado {g.moneda === "COP" ? copFormatter.format(montoTotal) : `${montoTotal.toLocaleString()}`}
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="text-xs max-w-[280px]">
                                          <p className="font-medium">Desglose contable</p>
                                          <p className="text-muted-foreground mt-1">
                                            Solo la <strong>Base</strong> capitaliza al inventario. El IVA es descontable y no se prorratea.
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                  {/* TSK-208: Tasa atenuada con tooltip de auditoría */}
                                  {g.moneda !== "COP" && hasRate && (
                                    <TooltipProvider delayDuration={200}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className={`text-[10px] tabular-nums cursor-help ${isManualTRM ? "text-primary font-medium" : "text-muted-foreground"}`}>
                                            × {rate!.toLocaleString()}
                                            {isManualTRM && " ✱"}
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="text-xs max-w-[240px]">
                                          <p className="font-medium">{rateOriginLabel}</p>
                                          <p className="text-muted-foreground mt-1">
                                            Tasa aplicada: <span className="font-mono">{rate!.toLocaleString()}</span> {g.moneda}/COP
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                                <span className="text-muted-foreground text-[11px]">→</span>
                                {hasRate ? (
                                  <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-400 text-right min-w-[110px]">
                                    {copFormatter.format(montoCOP!)}
                                  </span>
                                ) : (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge variant="outline" className="text-[10px] h-5 border-amber-500/50 text-amber-700 dark:text-amber-400 cursor-help">
                                          Sin TRM
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="text-xs max-w-[240px]">
                                        No hay tasa de cambio vigente para <strong>{g.moneda}</strong>. Cargue la TRM histórica o defina <strong>trm_pago</strong> al editar el gasto.
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {/* TSK-209: Botón de edición granular */}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => setEditingGasto(g)}
                                  title={isLiquidado ? "Ver gasto (liquidado)" : "Editar gasto"}
                                >
                                  <Pencil className="h-3 w-3 text-muted-foreground" />
                                </Button>
                                {!isLiquidado && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => deleteGasto.mutate({ id: g.id, embarque_id: embarque!.id })}
                                  >
                                    <Trash2 className="h-3 w-3 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Consolidated totals — Fase C: CBM nativo desde el RPC. */}
                  {(() => {
                    const gastosPesoCOP  = shipmentLC?.globals.gastosPesoCOP  ?? 0;
                    const gastosValorCOP = shipmentLC?.globals.gastosValorCOP ?? 0;
                    const gastosCBMCOP   = shipmentLC?.globals.gastosCBMCOP   ?? 0;
                    // Mostramos la columna CBM cuando hay gastos declarados
                    // como CBM (aunque el RPC todavía esté cargando), o cuando
                    // el RPC ya devolvió un subtotal CBM > 0.
                    return (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <p className="text-[11px] text-muted-foreground">Gastos por Peso</p>
                          <p className="text-sm font-semibold text-foreground">
                            {copFormatter.format(gastosPesoCOP)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] text-muted-foreground">Gastos por Valor</p>
                          <p className="text-sm font-semibold text-foreground">
                            {copFormatter.format(gastosValorCOP)}
                          </p>
                        </div>
                        <div>
                          <p
                            className="text-[11px] text-muted-foreground"
                            title="Subtotal nativo de la bolsa CBM (RPC v2 Fase C). Mientras los ítems no lleven volumen individual, CBM se prorratea proporcional al valor FOB."
                          >
                            Gastos por CBM
                          </p>
                          <p className="text-sm font-semibold text-foreground">
                            {copFormatter.format(gastosCBMCOP)}
                          </p>
                        </div>

                        <div>
                          <p className="text-[11px] text-muted-foreground">IVA (per-ítem)</p>
                          <p className="text-sm font-semibold text-foreground">
                            {copFormatter.format(ivaTotalCOP)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] text-muted-foreground">Total Gastos</p>
                          <p className="text-sm font-semibold text-primary">
                            {copFormatter.format(
                              gastosPesoCOP + gastosValorCOP + gastosCBMCOP + ivaTotalCOP
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* === Sub-Tabs: Documentos / Costeo (Nivel 3) === */}
              <Tabs value={innerTab} onValueChange={(v) => setInnerTab(v as "docs-soporte" | "costeo")} className="w-full">
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="docs-soporte" className="gap-1 text-xs">
                    <FileText className="h-3.5 w-3.5" />
                    Documentos Soporte
                  </TabsTrigger>
                  <TabsTrigger value="costeo" className="gap-1 text-xs">
                    📊 Costeo y Prorrateo
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="docs-soporte" className="mt-3 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground mr-1">Expediente:</span>
                    {REQUIRED_DOC_TYPES.map(dt => {
                      const present = presentDocTypes.has(dt.key.toLowerCase());
                      return (
                        <Badge
                          key={dt.key}
                          variant={present ? "default" : "outline"}
                          className={present
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : "border-destructive text-destructive"
                          }
                        >
                          {present ? "✅" : "⚠️"} {dt.label}
                        </Badge>
                      );
                    })}
                    <Badge variant="secondary">
                      <FileText className="h-3 w-3 mr-1" />
                      {documents.length} Total
                    </Badge>
                  </div>

                  {docsLoading ? (
                    <p className="text-muted-foreground text-sm py-4 text-center">Cargando documentos...</p>
                  ) : documents.length === 0 ? (
                    <p className="text-muted-foreground text-sm py-8 text-center">
                      No hay documentos asignados a este embarque.
                    </p>
                  ) : (
                    <DocumentsTable
                      documents={typedDocuments}
                      loading={docsLoading}
                      onFilteredIdsChange={handleFilteredIdsChange}
                      inventoryItems={allEmbarqueInventory}
                      disableEmbarqueDetail
                      highlightIds={highlightDocIds}
                      hideAuditColumn
                      onRowClick={(doc) => setAuditDocId(doc.id)}
                    />
                  )}
                </TabsContent>

                <TabsContent value="costeo" className="mt-3">
                  <InventoryTable
                    items={allEmbarqueInventory}
                    loading={invLoading}
                    linkedDocumentId={null}
                    documentType={selectedDocumentType || "Factura Internacional"}
                    externalLandedCosts={landedCostByItemId}
                    externalTrmAduana={trmAduana}
                    forceAuditMode={true}
                    onOpenTraceability={(itemId) => setTraceabilityItemId(itemId)}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>

          <TabsContent value="aduanas" className="flex-1 flex flex-col overflow-hidden min-h-0">
            <AduanasTab
              embarqueId={embarque.id}
              numeroDo={embarque.numero_do}
              orgId={orgId}
              documents={documents as any}
              isLiquidado={isLiquidado}
              onUploadDim={() => {
                handleClose();
                toast.info("Sube la DIM desde el panel principal", {
                  description: "Selecciona 'Declaración de Importación' como tipo y vincúlala a este embarque.",
                });
              }}
            />
          </TabsContent>

          <TabsContent value="tracking" className="flex-1 overflow-y-auto min-h-0">
            <PlanGate allowedPlans={['ENTERPRISE']} featureName="Tracking de Contenedores en Tiempo Real">
              <div className="pb-10">
                <TrackingTab
                  embarqueId={embarque.id}
                  trackingNumber={embarque.tracking_number}
                  shippingLine={embarque.shipping_line}
                  trackingHistory={embarque.tracking_history}
                />
              </div>
            </PlanGate>
          </TabsContent>
        </Tabs>
      </DialogContent>

      <CommentsSheet
        open={showComments}
        onOpenChange={setShowComments}
        entityId={embarque.id}
        entityType="embarque"
        entityLabel={editedTitle || embarque.numero_do}
      />

      {/* TSK-209: Side-sheet de edición granular de gasto */}
      <EditGastoSheet
        gasto={editingGasto}
        embarqueId={embarque.id}
        open={!!editingGasto}
        onOpenChange={(o) => { if (!o) setEditingGasto(null); }}
        systemRate={editingGasto && editingGasto.moneda !== "COP" ? effectiveTrmMap[editingGasto.moneda] ?? null : null}
        isLiquidado={isLiquidado}
      />

      {/* Barrera 1 — Firma de Excepción cuando hay mismatch de pesos */}
      <LiquidacionExcepcionDialog
        open={showExcepcionDialog}
        onOpenChange={setShowExcepcionDialog}
        weightRecon={weightRecon}
        isPending={freezeMutation.isPending}
        onConfirm={(excepcion) => handleLiquidar(excepcion)}
      />

      {/* Barrera 3 — Recibo de trazabilidad imprimible por ítem */}
      <ItemTraceabilityModal
        open={!!traceabilityItemId}
        onOpenChange={(o) => { if (!o) setTraceabilityItemId(null); }}
        item={allEmbarqueInventory.find((i: any) => i.id === traceabilityItemId) ?? null}
        isLiquidado={isLiquidado}
        livePerItem={traceabilityItemId ? landedCostByItemId[traceabilityItemId] ?? null : null}
        trmAduana={trmAduana}
        numeroDo={embarque.numero_do}
        gastos={gastos}
        allocations={
          traceabilityItemId
            ? (shipmentLC?.allocations ?? []).filter(a => a.itemId === traceabilityItemId)
            : []
        }
      />

      {/* Deep-link AuditPanel disparado desde CompliancePill (HS Code blockers) */}
      <AuditPanel
        doc={auditDoc}
        open={!!auditDoc}
        onOpenChange={(o) => { if (!o) setAuditDocId(null); }}
      />
    </Dialog>
  );
}
